import { useEffect, useState, useCallback, useRef } from 'react'
import { CheckCircleIcon, XCircleIcon, WarningIcon, InfoIcon, XIcon } from '@phosphor-icons/react'
import { ScoopButton } from '../ScoopButton/ScoopButton'
import styles from './ScoopToast.module.scss'

const SEVERITY_ICONS = {
    success: CheckCircleIcon,
    error: XCircleIcon,
    warning: WarningIcon,
    info: InfoIcon,
}

const DEFAULT_DURATION = 6000
const RESUME_DURATION = 1500 // Duration after mouse leave

/**
 * ScoopToast - Toast notification component
 *
 * @param {Object} props - Component props
 * @param {Object|null} props.alert - Alert object containing message and severity
 * @param {string} props.alert.message - Message to display
 * @param {'success'|'error'|'warning'|'info'} props.alert.severity - Alert severity
 * @param {Function} props.onClose - Callback when toast closes
 * @param {number} [props.duration] - Duration in ms (overrides autoHideDuration)
 * @param {number} [props.autoHideDuration=6000] - Auto hide duration in ms (legacy support)
 * @param {boolean} [props.persistent=false] - If true, toast won't auto-dismiss
 * @param {Object} [props.style] - Custom styles (used for z-index in stacking)
 * @param {Object} [props.position] - Position configuration
 * @param {'top'|'bottom'} [props.position.vertical='bottom'] - Vertical position
 * @param {'left'|'center'|'right'} [props.position.horizontal='left'] - Horizontal position
 */
export const ScoopToast = ({
    alert,
    onClose,
    duration,
    autoHideDuration = DEFAULT_DURATION,
    persistent = false,
    style,
    position = { vertical: 'bottom', horizontal: 'left' },
}) => {
    const [isVisible, setIsVisible] = useState(false)
    const [isExiting, setIsExiting] = useState(false)
    const timerRef = useRef(null)
    const remainingTimeRef = useRef(null)
    const startTimeRef = useRef(null)

    // Use duration prop if provided, otherwise fall back to autoHideDuration
    const effectiveDuration = duration ?? autoHideDuration

    const clearTimer = useCallback(() => {
        if (timerRef.current) {
            clearTimeout(timerRef.current)
            timerRef.current = null
        }
    }, [])

    const handleClose = useCallback(() => {
        clearTimer()
        setIsExiting(true)
        // Wait for exit animation to complete
        setTimeout(() => {
            setIsExiting(false)
            setIsVisible(false)
            onClose()
        }, 250) // Match transition duration
    }, [onClose, clearTimer])

    const startTimer = useCallback(
        (timeRemaining) => {
            if (persistent) return

            clearTimer()
            startTimeRef.current = Date.now()
            remainingTimeRef.current = timeRemaining

            timerRef.current = setTimeout(() => {
                handleClose()
            }, timeRemaining)
        },
        [persistent, handleClose, clearTimer]
    )

    // Show toast when alert changes
    useEffect(() => {
        if (alert) {
            setIsVisible(true)
            setIsExiting(false)
            remainingTimeRef.current = effectiveDuration
            startTimer(effectiveDuration)
        }

        return () => clearTimer()
    }, [alert, effectiveDuration, startTimer, clearTimer])

    // Handle hover pause
    const handleMouseEnter = useCallback(() => {
        if (persistent || !timerRef.current) return
        clearTimer()

        // Calculate remaining time
        if (startTimeRef.current && remainingTimeRef.current) {
            const elapsed = Date.now() - startTimeRef.current
            remainingTimeRef.current = Math.max(remainingTimeRef.current - elapsed, RESUME_DURATION)
        }
    }, [persistent, clearTimer])

    const handleMouseLeave = useCallback(() => {
        if (persistent) return
        // Resume with remaining time or a minimum duration
        const resumeTime = remainingTimeRef.current || RESUME_DURATION
        startTimer(resumeTime)
    }, [persistent, startTimer])

    if (!alert || !isVisible) return null

    const severity = alert.severity || 'info'
    const SeverityIcon = SEVERITY_ICONS[severity]
    const { vertical = 'bottom', horizontal = 'left' } = position

    const toastClasses = [
        styles.toast,
        styles[severity],
        styles[vertical],
        styles[horizontal],
        isVisible && !isExiting ? styles.visible : '',
        isExiting ? styles.exiting : '',
    ]
        .filter(Boolean)
        .join(' ')

    return (
        <div
            className={toastClasses}
            role="alert"
            aria-live="polite"
            style={style}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
        >
            <span className={styles.icon}>
                <SeverityIcon size={20} weight="fill" />
            </span>
            <span className={styles.message}>{alert.message}</span>
            <ScoopButton
                variant="text"
                size="xxs"
                onClick={handleClose}
                aria-label="Close notification"
                className={styles.closeButton}
            >
                <XIcon size={16} />
            </ScoopButton>
        </div>
    )
}

export default ScoopToast
